import React, { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import { useEvents } from "../lib/queries";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../components/ui/popover";
import {
  CalendarActions,
  CalendarDateHeader,
  CalendarEventLabel,
  CalendarEventPopover,
  calendarDateKey,
  eventColor,
} from "../components/calendar/CalendarEvent";
import CalendarYear from "../components/calendar/CalendarYear";
import { Calendar as DayCalendar } from "../components/ui/calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../styles/calendar.css";
import "../styles/reference-calendar.css";

const localizer = momentLocalizer(moment);
const components = {
  event: CalendarEventLabel,
  eventWrapper: CalendarEventPopover,
  month: { dateHeader: CalendarDateHeader },
};

export default function CalendarView() {
  const { data: rawEvents, isPending: loading, isError: error, refetch: refetchEvents } = useEvents();
  const events = useMemo(
    () =>
      (rawEvents || [])
        .map((event) => ({
          id: event.id,
          title: event.title,
          start: new Date(event.event_date_start),
          end: new Date(event.event_date_end || event.event_date_start),
          resource: event,
        }))
        .filter(
          (event) =>
            !Number.isNaN(event.start.getTime()) &&
            !Number.isNaN(event.end.getTime()),
        ),
    [rawEvents],
  );
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [compact, setCompact] = useState(
    () => window.matchMedia("(max-width: 640px)").matches,
  );
  useEffect(() => {
    const media = window.matchMedia("(max-width: 640px)");
    const update = () => setCompact(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const openDay = (date) => {
    setCurrentDate(date);
    setSelectedDate(date);
    setView("day");
  };
  const chooseDate = (date) => {
    setCurrentDate(date);
    setSelectedDate(date);
    setDatePickerOpen(false);
  };
  const navigate = (direction) => {
    const year = currentDate.getFullYear(),
      month = currentDate.getMonth();
    const next =
      view === "year"
        ? new Date(year + direction, month, 1)
        : view === "month"
          ? new Date(year, month + direction, 1)
          : new Date(year, month, currentDate.getDate() + direction);
    setCurrentDate(next);
    setSelectedDate(next);
  };
  const period =
    view === "year"
      ? String(currentDate.getFullYear())
      : currentDate.toLocaleDateString(
          "en",
          view === "day"
            ? { month: "long", day: "numeric", year: "numeric" }
            : { month: "long", year: "numeric" },
        );
  const visibleCount = useMemo(() => {
    const year = currentDate.getFullYear(),
      month = currentDate.getMonth(),
      day = currentDate.getDate();
    const start = new Date(
      year,
      view === "year" ? 0 : month,
      view === "day" ? day : 1,
    );
    const end =
      view === "year"
        ? new Date(year + 1, 0, 1)
        : view === "month"
          ? new Date(year, month + 1, 1)
          : new Date(year, month, day + 1);
    return events.filter((event) => event.start < end && event.end >= start)
      .length;
  }, [events, currentDate, view]);
  return (
    <Layout>
      <div className="calendar-page">
        <h1 className="sr-only">Event Calendar</h1>
        <section
          className="reference-calendar"
          aria-label="Event calendar"
          aria-busy={loading}
        >
          <div className="calendar-reference-toolbar">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  className="calendar-date-picker"
                  aria-label="Choose calendar date"
                >
                  <CalendarDays size={15} />
                  <span>
                    {selectedDate.toLocaleDateString("en", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" sideOffset={6} className="date-popover">
                <DayCalendar
                  mode="single"
                  selected={selectedDate}
                  defaultMonth={selectedDate}
                  onSelect={(day) => day && chooseDate(day)}
                  initialFocus
                />
                <div className="date-popover-foot">
                  <span />
                  <button type="button" onClick={() => chooseDate(new Date())}>
                    Today
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <div className="calendar-period-navigation">
              <button
                aria-label={`Previous ${view}`}
                onClick={() => navigate(-1)}
              >
                <ChevronLeft size={18} />
              </button>
              <h2 aria-live="polite">{period}</h2>
              <button aria-label={`Next ${view}`} onClick={() => navigate(1)}>
                <ChevronRight size={18} />
              </button>
            </div>
            <div
              className="calendar-view-switch"
              role="group"
              aria-label="Calendar view"
            >
              {["year", "month", "day"].map((mode) => (
                <button
                  key={mode}
                  aria-pressed={view === mode}
                  onClick={() => {
                    setView(mode);
                    if (mode === "day") setCurrentDate(selectedDate);
                  }}
                >
                  {mode[0].toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {error && (
            <div className="calendar-feedback" role="alert">
              Events could not be loaded.{" "}
              <button onClick={() => refetchEvents()}>Try again</button>
            </div>
          )}
          {loading ? (
            <div className="calendar-loading" role="status">
              <span />
              Loading calendar...
            </div>
          ) : (
            <CalendarActions.Provider
              value={{
                selectDate: setSelectedDate,
                selectedDate,
                openDay,
                compact,
              }}
            >
              {view === "year" ? (
                <CalendarYear
                  date={currentDate}
                  events={events}
                  onSelectMonth={(date) => {
                    chooseDate(date);
                    setView("month");
                  }}
                />
              ) : (
                <div className={`calendar-grid-shell calendar-grid-${view}`}>
                  <Calendar
                    localizer={localizer}
                    events={events}
                    date={currentDate}
                    view={view}
                    onNavigate={setCurrentDate}
                    onView={setView}
                    views={["month", "day"]}
                    toolbar={false}
                    components={components}
                    selectable
                    onSelectSlot={({ start }) => setSelectedDate(start)}
                    onDrillDown={openDay}
                    onShowMore={(_, date) => openDay(date)}
                    eventPropGetter={(event) => ({
                      style: {
                        backgroundColor: eventColor(event.resource),
                        "--event-color": eventColor(event.resource),
                      },
                    })}
                    dayPropGetter={(date) => ({
                      className:
                        calendarDateKey(date) === calendarDateKey(selectedDate)
                          ? "calendar-selected-date"
                          : "",
                    })}
                    formats={{ weekdayFormat: "ddd" }}
                  />
                </div>
              )}
            </CalendarActions.Provider>
          )}
          <div className="calendar-reference-footer">
            <span aria-live="polite">
              {loading
                ? "Loading events"
                : error
                  ? "Events unavailable"
                  : `${visibleCount} ${visibleCount === 1 ? "event" : "events"} this ${view}`}
            </span>
            <span>Select an event to view details</span>
          </div>
        </section>
      </div>
    </Layout>
  );
}
